/**
 * Schema Utilities for Dynamic Form Rendering
 * Parses JSON Schema and determines how to render form fields
 */

export interface SchemaProperty {
  name: string;
  type: string;
  required: boolean;
  description?: string;
  enum?: string[];
  format?: string;
  minimum?: number;
  maximum?: number;
  minLength?: number;
  maxLength?: number;
  default?: unknown;
  // For nested objects/arrays (future support)
  properties?: Record<string, SchemaProperty>;
  items?: SchemaProperty;
}

export type FormFieldType =
  | 'text'
  | 'email'
  | 'url'
  | 'number'
  | 'checkbox'
  | 'select'
  | 'textarea'
  | 'date'
  | 'datetime'
  | 'json'; // Fallback for complex types

/**
 * Parse schema properties into a structured format
 */
export const parseSchemaProperties = (
  schema: Record<string, unknown> | null
): SchemaProperty[] => {
  if (!schema) return [];

  const properties = schema.properties as Record<string, Record<string, unknown>> | undefined;
  if (!properties) return [];

  const required = (schema.required as string[]) || [];
  const result: SchemaProperty[] = [];

  for (const [name, prop] of Object.entries(properties)) {
    result.push({
      name,
      type: (prop.type as string) || 'string',
      required: required.includes(name),
      description: prop.description as string | undefined,
      enum: prop.enum as string[] | undefined,
      format: prop.format as string | undefined,
      minimum: prop.minimum as number | undefined,
      maximum: prop.maximum as number | undefined,
      minLength: prop.minLength as number | undefined,
      maxLength: prop.maxLength as number | undefined,
      default: prop.default,
    });
  }

  return result;
};

/**
 * Get the appropriate form field type for a schema property
 */
export const getFieldType = (property: SchemaProperty): FormFieldType => {
  // Enum values → select dropdown
  if (property.enum && property.enum.length > 0) {
    return 'select';
  }

  // Boolean → checkbox
  if (property.type === 'boolean') {
    return 'checkbox';
  }

  // Number/Integer → number input
  if (property.type === 'number' || property.type === 'integer') {
    return 'number';
  }

  // String with format
  if (property.type === 'string') {
    switch (property.format) {
      case 'email':
        return 'email';
      case 'uri':
      case 'url':
        return 'url';
      case 'date':
        return 'date';
      case 'date-time':
        return 'datetime';
      default:
        // Long strings might be better as textarea
        if (property.maxLength && property.maxLength > 200) {
          return 'textarea';
        }
        return 'text';
    }
  }

  // Object/Array → JSON editor fallback
  if (property.type === 'object' || property.type === 'array') {
    return 'json';
  }

  // Default to text
  return 'text';
};

/**
 * Check if a schema can be rendered as a form (vs needing JSON editor fallback)
 */
export const isSchemaRenderable = (schema: Record<string, unknown> | null): boolean => {
  if (!schema) return false;

  // Must be object type (or implicit object when properties is present)
  if (schema.type && schema.type !== 'object') return false;

  // Must have properties
  const properties = schema.properties as Record<string, Record<string, unknown>> | undefined;
  if (!properties || Object.keys(properties).length === 0) return false;

  // Check for unsupported keywords at root
  const unsupportedKeywords = ['allOf', 'anyOf', 'oneOf', 'not', '$ref', 'if', 'then', 'else'];
  for (const keyword of unsupportedKeywords) {
    if (keyword in schema) return false;
  }

  // All properties must be basic types (not deeply nested)
  for (const prop of Object.values(properties)) {
    const type = prop.type as string;
    // Allow object/array but they'll render as JSON
    if (type === 'object' || type === 'array') {
      // Check if nested object has its own complex schema
      if (prop.properties && typeof prop.properties === 'object') {
        // Has nested structure - we can still render but with JSON fallback
        continue;
      }
    }
  }

  return true;
};

/**
 * Generate default values for a schema
 */
export const getDefaultValues = (
  schema: Record<string, unknown> | null
): Record<string, unknown> => {
  if (!schema) return {};

  const properties = schema.properties as Record<string, Record<string, unknown>> | undefined;
  if (!properties) return {};

  const defaults: Record<string, unknown> = {};

  for (const [name, prop] of Object.entries(properties)) {
    // Use explicit default if provided
    if (prop.default !== undefined) {
      defaults[name] = prop.default;
      continue;
    }

    // Otherwise use type-appropriate defaults
    const type = prop.type as string;
    switch (type) {
      case 'string':
        defaults[name] = '';
        break;
      case 'number':
      case 'integer':
        defaults[name] = prop.minimum !== undefined ? prop.minimum : '';
        break;
      case 'boolean':
        defaults[name] = false;
        break;
      case 'array':
        defaults[name] = [];
        break;
      case 'object':
        defaults[name] = {};
        break;
      default:
        defaults[name] = '';
    }
  }

  return defaults;
};

/**
 * Convert form values to the correct types based on schema
 */
export const convertFormValues = (
  values: Record<string, unknown>,
  schema: Record<string, unknown> | null
): Record<string, unknown> => {
  if (!schema) return values;

  const properties = schema.properties as Record<string, Record<string, unknown>> | undefined;
  if (!properties) return values;

  const converted: Record<string, unknown> = {};

  for (const [name, value] of Object.entries(values)) {
    const prop = properties[name];
    if (!prop) {
      converted[name] = value;
      continue;
    }

    const type = prop.type as string;

    // Convert string values to appropriate types
    if (typeof value === 'string') {
      switch (type) {
        case 'number':
          converted[name] = value === '' ? undefined : parseFloat(value);
          break;
        case 'integer':
          converted[name] = value === '' ? undefined : parseInt(value, 10);
          break;
        case 'boolean':
          converted[name] = value === 'true' || value === '1';
          break;
        default:
          converted[name] = value || undefined;
      }
    } else {
      converted[name] = value;
    }
  }

  // Remove undefined values
  return Object.fromEntries(
    Object.entries(converted).filter(([, v]) => v !== undefined && v !== '')
  );
};
